/**
 * parseServerError — normalise server error bodies into { code, message }.
 *
 * Lives in its own module (not client.ts) so it can load under jest —
 * client.ts's module-scope import.meta.env read can't (same pattern as
 * sessionExpired.ts / errorLogLevel.ts / networkError.ts). client.ts imports
 * and re-throws the result as ApiError, whose message forms render verbatim.
 *
 * Recognised shapes:
 *   { error: { code, message, feature } }     — checkWriteAccess / apiError()
 *   { error: 'plan_limit_exceeded', message } — checkIssue/ProjectPlanLimit
 *   { success: false, error: ZodError }       — hono zValidator default 400
 *
 * The zValidator shape is why this module exists (2026-08-04): a serialized
 * ZodError carries `issues` but NO `message` (message is a prototype getter
 * JSON.stringify drops), so validation 400s used to fall through to the
 * generic 'API request failed' — a 150-char project name produced a toast
 * that never mentioned the name. The wire shape is pinned server-side in
 * packages/server/src/__tests__/settings/project-crud.test.ts.
 */

const GENERIC_MESSAGE = 'API request failed';

interface ZodIssueLike {
  code?: string;
  message?: string;
  path?: unknown[];
  maximum?: number;
  minimum?: number;
  type?: string;
}

/** 'primaryDomain' -> 'Primary domain'; 'name' -> 'Name'. */
function fieldLabel(path: unknown[] | undefined): string | null {
  const segment = [...(path ?? [])].reverse().find((p): p is string => typeof p === 'string');
  if (!segment) return null;
  const spaced = segment.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Render the FIRST issue only so the toast stays one readable sentence. */
function messageFromZodIssues(issues: ZodIssueLike[]): string | null {
  const issue = issues[0];
  if (!issue) return null;
  const label = fieldLabel(issue.path);
  if (label && issue.code === 'too_big' && issue.type === 'string' && typeof issue.maximum === 'number') {
    return `${label} must be at most ${issue.maximum} characters.`;
  }
  if (label && issue.code === 'too_small' && issue.type === 'string' && issue.minimum === 1) {
    return `${label} is required.`;
  }
  if (label && issue.message) {
    return `${label}: ${issue.message}`;
  }
  return issue.message || null;
}

export function parseServerError(data: unknown): { code?: string; message: string } {
  const body = (data ?? {}) as { error?: unknown; message?: string };
  const err = body.error;
  if (err && typeof err === 'object') {
    const e = err as { code?: string; message?: string; issues?: unknown };
    if (e.code || e.message) {
      return { code: e.code, message: e.message || body.message || GENERIC_MESSAGE };
    }
    if (Array.isArray(e.issues)) {
      const message = messageFromZodIssues(e.issues as ZodIssueLike[]);
      if (message) return { code: 'VALIDATION_ERROR', message };
    }
    return { message: body.message || GENERIC_MESSAGE };
  }
  if (typeof err === 'string') {
    return { code: err, message: body.message || GENERIC_MESSAGE };
  }
  return { message: body.message || GENERIC_MESSAGE };
}
